import { useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { RefreshCw, CheckCircle, XCircle, Loader2, RotateCcw } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { calculateScores } from '@/services/scoring/calculateScores';
import { Separator } from '@/components/ui/separator';

const ASSESSMENTS = [
  { id: '441bd6aa-cf50-42c9-b889-a3a8aec5da30', name: 'mySuccession Snapshot' },
  { id: '30d8b24c-8a95-45e5-867e-82bb9c335d1e', name: 'Owner Readiness and Exit Timeline' },
  { id: 'e7fbf925-95c7-4758-905a-3fca4adbadc3', name: 'Business Profile' },
  { id: '7738f29f-81bd-41d4-8ba2-d0c7086140df', name: 'Business Valuation' },
  { id: '84d55edf-0b78-4e4c-bbec-8a293a865d33', name: 'Business Optimization Review' },
  { id: 'e609cfc8-836a-4422-9a74-842a368b46be', name: 'Advisory Team Assembly (A6)' },
  { id: 'f64bb6aa-75a5-4ea9-89db-5916c386a965', name: 'Successor Strategy' },
  { id: 'be7fc1b7-b18e-4e02-932f-0451158c1ad9', name: 'Governance and Family Dynamics' },
  { id: '99c58da2-89af-4e89-bc0e-61791830d298', name: 'Exit Structure and Tax Optimization' },
];

type RunState = 'idle' | 'loading' | 'success' | 'error';
type ResetState = 'idle' | 'loading' | 'success' | 'error';

interface RunResult {
  overallScore: number;
  mcfCount?: number;
  message: string;
  mcfBreakdown?: Array<{ code: string; score: number | null }>;
}

export function ScoringTools() {
  const [email, setEmail] = useState('');
  const [assessmentId, setAssessmentId] = useState('');
  const [runState, setRunState] = useState<RunState>('idle');
  const [resetState, setResetState] = useState<ResetState>('idle');
  const [resetMessage, setResetMessage] = useState('');
  const [result, setResult] = useState<RunResult | null>(null);

  const resolveUserId = async (emailVal: string): Promise<string | null> => {
    // Use SECURITY DEFINER function to bypass RLS for admin lookups
    const { data, error } = await supabase.rpc('admin_lookup_user_by_email', { lookup_email: emailVal });
    if (error) {
      console.error('[ScoringTools] admin_lookup_user_by_email failed:', error);
      // Fallback to direct profile query (works if admin is viewing own profile)
      const { data: p1 } = await supabase.from('profiles').select('user_id').eq('business_email', emailVal).maybeSingle();
      if (p1?.user_id) return p1.user_id;
      const { data: p2 } = await supabase.from('profiles').select('user_id').eq('personal_email', emailVal).maybeSingle();
      return p2?.user_id ?? null;
    }
    return data ?? null;
  };

  const handleResetToIncomplete = async () => {
    if (!email.trim() || !assessmentId) return;
    setResetState('loading');
    setResetMessage('');

    try {
      const userId = await resolveUserId(email.trim());
      if (!userId) {
        setResetState('error');
        setResetMessage(`No profile found for: ${email}`);
        return;
      }

      // Find the module_id for this assessment
      const { data: assessment } = await supabase
        .from('assessments')
        .select('module_id')
        .eq('id', assessmentId)
        .maybeSingle();
      const moduleId = assessment?.module_id;

      if (!moduleId) {
        setResetState('error');
        setResetMessage('Could not resolve module_id for selected assessment.');
        return;
      }

      // Reset assessment_modules status back to 'in-progress'
      const { error: modErr } = await supabase
        .from('assessment_modules')
        .update({ status: 'in-progress', completion_date: null, final_score: null })
        .eq('user_id', userId)
        .eq('module_id', moduleId);

      // Reset assessment_progress is_complete flag
      await supabase
        .from('assessment_progress')
        .update({ is_complete: false, completion_percentage: 0 })
        .eq('user_id', userId)
        .eq('module_id', moduleId);

      if (modErr) {
        setResetState('error');
        setResetMessage(`Failed to reset: ${modErr.message}`);
        return;
      }

      setResetState('success');
      setResetMessage(`Assessment reset to "in-progress" for ${email}. User can now re-take it.`);
    } catch (err: any) {
      setResetState('error');
      setResetMessage(err?.message || 'Unexpected error during reset.');
    }
  };

  const handleRerunScoring = async () => {
    if (!email.trim() || !assessmentId) return;

    setRunState('loading');
    setResult(null);

    try {
      const userId = await resolveUserId(email.trim());

      if (!userId) {
        setRunState('error');
        setResult({ overallScore: 0, message: `No profile found for email: ${email}` });
        return;
      }

      // Check response count before scoring
      const { count: responseCount } = await supabase
        .from('user_assessment_responses')
        .select('id', { count: 'exact', head: true })
        .eq('user_id', userId)
        .eq('assessment_id', assessmentId);

      if (!responseCount || responseCount === 0) {
        setRunState('error');
        setResult({ overallScore: 0, message: `No responses found for this user + assessment combination.` });
        return;
      }

      // Run scoring
      const { overallScore, success } = await calculateScores(userId, null, assessmentId);

      // Fetch MCF breakdown for the relevant section
      const { data: mcfRows } = await supabase
        .from('user_mcf_values')
        .select('mcf_id, calculated_value, measurement_component_factors!inner(code)')
        .eq('user_id', userId);

      const mcfBreakdown = (mcfRows || []).map((r: any) => ({
        code: r.measurement_component_factors?.code ?? r.mcf_id,
        score: (r.calculated_value as any)?.score ?? null,
      })).sort((a: any, b: any) => a.code.localeCompare(b.code));

      if (success) {
        setRunState('success');
        setResult({
          overallScore,
          mcfCount: mcfRows?.length ?? 0,
          message: `Scoring complete. ${responseCount} responses processed. ${mcfRows?.length ?? 0} total MCF values in database.`,
          mcfBreakdown,
        });
      } else {
        setRunState('error');
        setResult({ overallScore: 0, message: 'Scoring engine returned failure. Check console for details.' });
      }
    } catch (err: any) {
      setRunState('error');
      setResult({ overallScore: 0, message: err?.message || 'Unexpected error during scoring.' });
    }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <RefreshCw className="h-5 w-5 text-primary" />
          Scoring Recalculation
        </CardTitle>
        <CardDescription>
          Re-run the scoring engine for a specific user and assessment using their existing responses.
          Use this after fixing scoring bugs to update MCF values without requiring the user to redo the assessment.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="grid gap-4 sm:grid-cols-2">
          <div className="space-y-2">
            <Label htmlFor="user-email">User Email</Label>
            <Input
              id="user-email"
              type="email"
              placeholder="user@example.com"
              value={email}
              onChange={e => setEmail(e.target.value)}
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="assessment-select">Assessment</Label>
            <Select value={assessmentId} onValueChange={setAssessmentId}>
              <SelectTrigger id="assessment-select">
                <SelectValue placeholder="Select assessment…" />
              </SelectTrigger>
              <SelectContent>
                {ASSESSMENTS.map(a => (
                  <SelectItem key={a.id} value={a.id}>{a.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>

        <div className="flex flex-wrap gap-2">
          <Button
            onClick={handleRerunScoring}
            disabled={!email.trim() || !assessmentId || runState === 'loading'}
          >
            {runState === 'loading' ? (
              <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Running…</>
            ) : (
              <><RefreshCw className="h-4 w-4 mr-2" /> Re-run Scoring</>
            )}
          </Button>

          <Button
            variant="outline"
            onClick={handleResetToIncomplete}
            disabled={!email.trim() || !assessmentId || resetState === 'loading'}
          >
            {resetState === 'loading' ? (
              <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Resetting…</>
            ) : (
              <><RotateCcw className="h-4 w-4 mr-2" /> Reset to Incomplete</>
            )}
          </Button>
        </div>

        {resetMessage && (
          <div className={`flex items-start gap-3 p-3 rounded-lg border text-sm ${
            resetState === 'success'
              ? 'border-primary/30 bg-primary/10'
              : 'border-destructive/30 bg-destructive/10'
          }`}>
            {resetState === 'success'
              ? <CheckCircle className="h-4 w-4 text-primary shrink-0 mt-0.5" />
              : <XCircle className="h-4 w-4 text-destructive shrink-0 mt-0.5" />}
            <span>{resetMessage}</span>
          </div>
        )}

        {result && (
          <div className={`flex items-start gap-3 p-4 rounded-lg border ${
              runState === 'success'
                ? 'border-primary/30 bg-primary/10'
                : 'border-destructive/30 bg-destructive/10'
            }`}>
              {runState === 'success' ? (
                <CheckCircle className="h-5 w-5 text-primary shrink-0 mt-0.5" />
              ) : (
                <XCircle className="h-5 w-5 text-destructive shrink-0 mt-0.5" />
              )}
              <div className="space-y-2 w-full">
                <p className="text-sm font-medium">{result.message}</p>
                {runState === 'success' && (
                  <>
                    <div className="flex gap-2 flex-wrap">
                      <Badge variant="outline">Overall: {result.overallScore.toFixed(1)}%</Badge>
                      <Badge variant="outline">{result.mcfCount} total MCF values</Badge>
                    </div>
                    {result.mcfBreakdown && result.mcfBreakdown.length > 0 && (
                      <>
                        <Separator className="my-2" />
                        <p className="text-xs font-medium text-muted-foreground">MCF Breakdown (all sections)</p>
                        <div className="grid grid-cols-2 sm:grid-cols-3 gap-1 max-h-48 overflow-y-auto">
                          {result.mcfBreakdown.map(m => (
                            <div key={m.code} className="flex items-center justify-between text-xs px-2 py-1 rounded bg-background border border-border">
                              <span className="font-mono text-muted-foreground truncate mr-1">{m.code}</span>
                              <span className={m.score === null ? 'text-destructive' : 'text-primary font-medium'}>
                                {m.score === null ? 'null' : `${m.score}`}
                              </span>
                            </div>
                          ))}
                        </div>
                      </>
                    )}
                  </>
                )}
              </div>
            </div>
        )}
      </CardContent>
    </Card>
  );
}
